import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminApi } from '@utils/adminApi';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
    const [loading, setLoading] = useState(true);

    // Bootstrap: validate stored token on mount
    useEffect(() => {
        const init = async () => {
            if (!token) { setLoading(false); return; }
            try {
                const { data } = await adminApi.get('/api/auth/me');
                setUser(data.user);
            } catch {
                localStorage.removeItem('admin_token');
                setToken(null);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [token]);

    const login = useCallback(async (email, password) => {
        const { data } = await adminApi.post('/api/auth/login', { email, password });
        localStorage.setItem('admin_token', data.token);
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (name, email, password) => {
        const { data } = await adminApi.post('/api/auth/register', { name, email, password });
        localStorage.setItem('admin_token', data.token);
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try { await adminApi.post('/api/auth/logout'); } catch { /* ignore */ }
        localStorage.removeItem('admin_token');
        setToken(null);
        setUser(null);
    }, []);

    return (
        <AdminAuthContext.Provider value={{ user, token, loading, login, register, logout }}>
            {children}
        </AdminAuthContext.Provider>
    );
}

export const useAdminAuth = () => {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
    return ctx;
};
